import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Card from 'components/card';
import { MdClose, MdCheck, MdOpenInNew } from 'react-icons/md';
import { getPurchase, markReceived, updatePurchase } from 'interfaces/purchases';
import { getSuppliers } from 'interfaces/supplier';
import { toast } from 'react-toastify';
import { updateProduct, updateProductPrice } from 'interfaces/products';
import SaveStatus from "components/actions/SaveStatus";

interface UpdatePurchaseModalProps {
  id: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const UpdatePurchaseModal: React.FC<UpdatePurchaseModalProps> = ({ id, isOpen, onClose, onUpdated }) => {
  const [po, setPo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [receptionDate, setReceptionDate] = useState('');
  const [tempReception, setTempReception] = useState('');
  const [savingReception, setSavingReception] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markingReceived, setMarkingReceived] = useState(false);

  const [lotNumbers, setLotNumbers] = useState<{[key:number]: string}>({});
  const [areas, setAreas] = useState<{[key:number]: string}>({});
  const [dlcs, setDlcs] = useState<{[key:number]: string}>({});
  const [quantities, setQuantities] = useState<{[key:number]: number}>({});
  const [prices, setPrices] = useState<{[key:number]: number}>({});

  const [autoSaving, setAutoSaving] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [priceUpdated, setPriceUpdated] = useState<{ [key:number]: boolean }>({});


  const autoSaveTimer = useRef<number | null>(null);
  const ignoreAutoSaveRef = useRef(false); // when true auto-save won't run

  const hasQtyPriceChanged = useMemo(() => {
    const items = po?.items || [];
    return items.some((it: any, i: number) => {
      const baseQ = Number(it.quantity ?? it.qty ?? 0);
      const baseP = Number(it.price ?? it.unitPrice ?? 0);
      const curQ = Number(quantities[i] ?? baseQ);
      const curP = Number(prices[i] ?? baseP);
      const norm = (n: number) => (Number.isFinite(n) ? n : 0);
      return norm(curQ) !== norm(baseQ) || norm(curP) !== norm(baseP);
    });
  }, [po?.items, quantities, prices]);

  const toYMD = (v: any) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    try {
      return new Date(v).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };

  const formatMoney = (v: any) =>
    Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

  const getSupplierName = (s: any) => {
    if (!s) return '—';
    if (typeof s === 'string') return s;
    return s.name || s.displayName || s.company || '—';
  };

  const getVatRate = (it: any) => {
    const raw = it?.vat ?? it?.vatPurchase ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const getProdId = (it: any) =>
    it?.productId?._id || it?.productId;

  const load = useCallback(async () => {
    if (!id || !isOpen) return;
    setLoading(true);
    try {
      const supRes: any = await getSuppliers();
      setSuppliers(supRes?.docs || supRes?.data || supRes || []);
      const res: any = await getPurchase(id);
      if (!res) {
        toast.error('Bon introuvable');
        setLoading(false);
        return;
      }
      setPo(res);

      const rec = res.receptionDate;
      setReceptionDate(rec ? new Date(rec).toISOString().split('T')[0] : '');

      setTempReception(res.tempReception ?? '');

      // initialize per-line state safely
      const initialLotNumbers: {[key:number]: string} = {};
      const initialDlcs: {[key:number]: string} = {};
      const initialAreas: {[key:number]: string} = {};
      const qts: {[key:number]: number} = {};
      const prs: {[key:number]: number} = {};

      (res.items || []).forEach((item: any, index: number) => {
        initialLotNumbers[index] = item.lotNumber || '';
        initialDlcs[index] = item.dlc ? toYMD(item.dlc) : '';
        initialAreas[index] = item.area || 'zone-a';
        qts[index] = Number(item.quantity ?? item.qty ?? 0) || 0;
        prs[index] = Number(item.price ?? item.unitPrice ?? 0) || 0;
      });

      setLotNumbers(initialLotNumbers);
      setDlcs(initialDlcs);
      setAreas(initialAreas);
      setQuantities(qts);
      setPrices(prs);

      // when purchase is already received, ensure autosave disabled
      if (res.status === 'received') ignoreAutoSaveRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [id, isOpen]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const supplierName =
    getSupplierName(
      po?.supplier ||
        suppliers.find((s: any) => (s._id) === (po?.supplierId || po?.supplier_id))
    );

  const deliveryPlanned =
    po?.deliveryPurchase ||
    po?.eta ||
    po?.deliveryDate ||
    null;

  // build items payload used by manual saves, mark received etc.
  const buildItemsPayload = () =>
    (po.items || []).map((item: any, index: number) => {
      const q = Number(quantities[index] ?? item.quantity);
      const newPrice = Number(prices[index] ?? item.price);
      return {
        productId: getProdId(item),
        quantity: q,
        price: newPrice,
        lotNumber: lotNumbers[index] || item.lotNumber || '',
        area: areas[index] || item.area || 'zone-a',
        dlc: dlcs[index] ? new Date(dlcs[index]).toISOString() : (item.dlc ? new Date(item.dlc).toISOString() : ''),
      };
    });

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  const { totalArticlesHT, totalTVA, totalHT, totalTTC } = useMemo(() => {
    const items = po?.items || [];
    let ht = 0;
    let tva = 0;
    items.forEach((it: any, i: number) => {
      const q = Number(quantities[i] ?? it.quantity);
      const pu = Number(prices[i] ?? it.price);
      const lineHT = (isNaN(q) ? 0 : q) * (isNaN(pu) ? 0 : pu);
      const vatRate = Number(getVatRate(it)) / 100;
      const lineTVA = lineHT * (vatRate > 0 ? vatRate : 0);
      ht += lineHT;
      tva += lineTVA;
    });
    const fees = Number(po?.deliveryFees ?? 0);
    const totalHT = round2(ht + (Number.isFinite(fees) ? fees : 0));
    const totalTTC = round2(totalHT + tva);
    return {
      totalArticlesHT: round2(ht),
      totalTVA: round2(tva),
      totalHT,
      totalTTC,
    };
  }, [po?.items, quantities, prices]);

  const getMagicToken = (p: any) => {
       if (!p) return null;
       if (p.magicLinkUuid) return p.magicLinkUuid;
       const url = p.magicLink || '';
       if (!url) return null;
       const parts = String(url).split('/');
       return parts[parts.length - 1] || null;
     };
     const magicToken = useMemo(() => getMagicToken(po), [po]);
     const handleOpenMagicLink = () => {
       if (!magicToken) {
         toast.error('Lien fournisseur indisponible');
         return;
       }
       window.open(`/view-purchase/${magicToken}`, '_blank');
     };

  // Sync product prices (manual - kept)
const syncProductsPrices = async () => {
    const items = po?.items || [];

    const updates = items.map(async (item: any, index: number) => {
      const productId = getProdId(item);
      const newPrice = Number(prices[index] ?? item.price);
      const oldPrice = Number(item.price);

      if (!productId || !isFinite(newPrice) || newPrice <= 0) return null;
      if (oldPrice === newPrice) return null;

      try {
        await updateProduct({
          _id: productId,
          purchasePrice: newPrice,
        });
        return productId;
      } catch (err) {
        console.warn(`⚠️ Impossible de mettre à jour le produit ${productId}`);
        return null;
      }
    });

    await Promise.allSettled(updates);
  };

  // --- AUTO-SAVE ---
  useEffect(() => {
    if (!po || !isOpen) return;
    if (!hasQtyPriceChanged) return;

    const timer = setTimeout(async () => {
      try {
        setAutoSaving(true);
        setAutoSaveError(null);

        // Construire payload items en ne modifiant que ce qui doit l'être
        const itemsPatch = (po.items || []).map((item: any, index: number) => {
          const quantity = Number(quantities[index] ?? item.quantity ?? 0);
          const price = Number(prices[index] ?? item.price ?? 0);
          return {
            ...item,
            quantity,
            price,
            lotNumber: lotNumbers[index] ?? item.lotNumber ?? '',
            area: areas[index] ?? item.area ?? 'zone-a',
            dlc: dlcs[index] ? new Date(dlcs[index]).toISOString() : item.dlc ?? '',
          };
        });


        // Calculer totalHT et totalPrice (TTC) en respectant la TVA
        const totalHT = itemsPatch.reduce((sum: any, it: any) => sum + (Number(it.quantity) * Number(it.price)), 0);
        const totalVAT = itemsPatch.reduce((sum: any, it: any) => sum + ((Number(it.quantity) * Number(it.price)) * (Number(it.vat ?? 0) / 100)), 0);
        const totalPrice = totalHT + totalVAT;

        const payload: any = {
          _id: po._id,
          items: itemsPatch,
          totalHT: round2(totalHT),
          totalPrice: round2(totalPrice),
        };

        const updated = await updatePurchase(payload);
        if (!updated || updated.error) throw new Error('update failed');

        setLastSavedAt(Date.now());
        onUpdated?.();
      } catch (e) {
        setAutoSaveError('Erreur de sauvegarde');
      } finally {
        setAutoSaving(false);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [hasQtyPriceChanged, po?._id, isOpen, quantities, prices, lotNumbers, areas, dlcs, onUpdated]);


  useEffect(() => {
    if (!po || !prices) return;

    Object.keys(prices).forEach((key) => {
      const i = Number(key);
      const newPrice = Number(prices[i]);
      const oldPrice = Number(po.items[i]?.price ?? 0);

      if (newPrice !== oldPrice && !isNaN(newPrice)) {
        // Déclenchement AutoSave Produit (nouveau prix d'achat)
        (async () => {
          try {
            await updateProductPrice(getProdId(po.items[i]), newPrice);

            setPriceUpdated(prev => ({ ...prev, [i]: true }));
            setTimeout(() => {
              setPriceUpdated(prev => ({ ...prev, [i]: false }));
            }, 1500);
          } catch (err) {
            console.error("Erreur AutoSave prix produit", err);
          }
        })();
      }
    });
  }, [prices]);

  // --- Save full reception (manual) ---
  const handleSaveReception = async () => {
    if (!po) return;
    // require date
    if (!receptionDate) {
      toast.error('Veuillez renseigner une date de réception avant de sauvegarder.');
      return;
    }
    setSavingReception(true);
    ignoreAutoSaveRef.current = true;
    try {
      const payload: any = {
        _id: po._id,
        receptionDate: new Date(receptionDate).toISOString(),
        tempReception: tempReception !== '' ? Number(tempReception) : undefined,
        items: buildItemsPayload(),
        totalHT: round2(totalHT),
        totalPrice: round2(totalTTC),
      };
      const updated = await updatePurchase(payload);
      if (!updated || updated.error) {
        toast.error('Erreur mise à jour');
        return;
      }
      await syncProductsPrices();
      toast.success('Réception enregistrée');
      onUpdated();
      load();
    } finally {
      setSavingReception(false);
      ignoreAutoSaveRef.current = false;
      onClose();
    }
  };

  const handleMarkReceived = async () => {
    if (!po) return;
    if (po.status === 'received') return;
    if (!receptionDate) {
      toast.error('Veuillez renseigner une date de réception avant de marquer reçu.');
      return;
    }
  
    setMarkingReceived(true);
    ignoreAutoSaveRef.current = true;
    if (autoSaveTimer.current) {
      window.clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
  
    try {
      const id = po._id;
  
      // 🛠 1️⃣ Préparer les items COMPLETS (lot, dlc, area, qty, price)
      const itemsPatch = buildItemsPayload();
  
      // 💰 Calculer les totaux comme dans SaveReception
      const totalHT = itemsPatch.reduce((sum: any, it: any) => sum + Number(it.quantity) * Number(it.price), 0);
      const totalVAT = itemsPatch.reduce((sum: any, it: any) => sum + (Number(it.quantity) * Number(it.price) * (Number(po.items?.[0]?.vat ?? 0) / 100)), 0);
      const totalPrice = totalHT + totalVAT;
  
      // 📝 2️⃣ Sauvegarder la réception complète AVANT markReceived
      const upd = await updatePurchase({
        _id: id,
        id,
        receptionDate: new Date(receptionDate).toISOString(),
        items: itemsPatch,
        totalHT: round2(totalHT),
        totalPrice: round2(totalPrice),
      });
      if (!upd || upd.error) {
        toast.error('Erreur lors de la sauvegarde des données de réception');
        return;
      }
  
      // 🎯 3️⃣ Passer en statut reçu
      const res = await markReceived(id);
      if (!res || res.error) {
        toast.error("Erreur lors du passage au statut 'reçu'");
        return;
      }
  
      await syncProductsPrices();
      toast.success('Marqué reçu');
      onUpdated();
      load();
    } finally {
      setMarkingReceived(false);
      ignoreAutoSaveRef.current = false;
      onClose();
    }
  };
  

  // // change only status to 'received' + reception date
  // const handleMarkReceived = async () => {
  //   if (!po) return;
  //   if (po.status === 'received') return;
  //   if (!receptionDate) {
  //     toast.error('Veuillez renseigner une date de réception avant de marquer reçu.');
  //     return;
  //   }

  //   setMarkingReceived(true);
  //   ignoreAutoSaveRef.current = true; // stop autosave while we operate
  //   // clear pending timer
  //   if (autoSaveTimer.current) {
  //     window.clearTimeout(autoSaveTimer.current);
  //     autoSaveTimer.current = null;
  //   }

  //   try {
  //       const id = po._id;
  //       // 1) Màj date de réception
  //       const upd = await updatePurchase({
  //         _id: id,
  //         id,
  //         receptionDate: new Date(receptionDate).toISOString(),
  //       });
  //       if (!upd || upd.error) {
  //         toast.error('Erreur lors de la mise à jour de la date de réception');
  //         return;
  //       }

  //       const res = await markReceived(id);
  //       if (!res || res.error) {
  //         toast.error("Erreur lors du passage au statut 'reçu'");
  //         return;
  //       }

  //       await syncProductsPrices();
  //       toast.success('Marqué reçu');
  //       onUpdated();
  //       load();
  //     } finally {
  //       setMarkingReceived(false);
  //       ignoreAutoSaveRef.current = false;
  //       onClose();
  //     }
  //   };

  const handleMarkPaid = async () => {
    if (!po || po.pay) return;
    setMarkingPaid(true);
    try {
      const updated = await updatePurchase({
        _id: po._id,
        pay: true,
      });
      if (updated && !updated.error) {
        toast.success('Marqué payé');
        onUpdated();
        load();
      } else toast.error('Erreur paiement');
    } finally {
      setMarkingPaid(false);
    }
  };

  // disable editing when already received
  const isLocked = po?.status === 'received';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <Card extra="p-6 border border-gray-200 rounded-2xl shadow-lg bg-white dark:bg-navy-800">
          {loading && (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin h-6 w-6 border-b-2 border-brand-500 rounded-full mr-2" />
              <p className="text-sm text-gray-500">Chargement…</p>
            </div>
          )}

          {!loading && po && (
            <>
              {/* Header */}
              <div className="flex justify-between items-start mb-6 border-b border-gray-200 pb-4">
                <div>
                  <h2 className="text-xl font-semibold text-navy-700">
                    Bon de commande <span className="text-[#0260CB]">{po.purchaseId}</span>
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Fournisseur : <span className="font-medium text-gray-900">{supplierName}</span>
                  </p>
                  {po.createdBy && (
                    <p className="text-xs text-gray-400 mt-1">
                      Créé par : {typeof po.createdBy === 'string' ? po.createdBy : po.createdBy?.fullName || po.createdBy?.email || '—'}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      Statut : {po.status || '—'}
                    </span>
                    {po.pay ? (
                     <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-200 text-emerald-900">
                       Payé
                     </span>
                   ) : (
                     <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                       En attente de paiement
                     </span>
                   )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <SaveStatus
                    status={
                      po.status === 'received'
                        ? 'saved' // lock visual state when received
                        : autoSaving
                          ? 'saving'
                          : autoSaveError
                            ? 'error'
                            : lastSavedAt
                              ? 'saved'
                              : 'idle'
                    }
                  />
                  <button
                   onClick={handleOpenMagicLink}
                   disabled={!magicToken}
                   title="Voir la page fournisseur"
                   className="px-3 py-1 rounded-md border text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
                 >
                   <MdOpenInNew className="w-4 h-4" />
                   Lien fournisseur
                 </button>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Infos */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Date commande</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {po.purchaseDate ? new Date(po.purchaseDate).toLocaleDateString('fr-FR') : po.createdAt ? new Date(po.createdAt).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Livraison prévue</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {deliveryPlanned ? new Date(deliveryPlanned).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Réception (date) *</label>
                  <input
                    type="date"
                    value={receptionDate}
                    onChange={(e) => setReceptionDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                    disabled={isLocked}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Température réception (°C)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={tempReception}
                    onChange={(e) => setTempReception(e.target.value)}
                    placeholder="Ex: 3.2"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                    disabled={isLocked}
                  />
                </div>
              </div>

              {/* Articles */}
              <div className="mb-8">
                <h3 className="text-base font-semibold text-navy-700 mb-3">Articles</h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="w-full text-xs text-gray-700">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left py-3 px-4">Produit</th>
                        <th className="text-right py-3 px-4">Quantité</th>
                        <th className="text-right py-3 px-4">Prix unitaire</th>
                        <th className="text-right py-3 px-4">Sous-total</th>
                        <th className="text-left py-3 px-4">Numéro de lot</th>
                        <th className="text-left py-3 px-4">DLC</th>
                        <th className="text-left py-3 px-4">Zone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(po.items || []).map((it: any, i: number) => {
                        const q = Number(quantities[i] ?? it.quantity);
                        const pu = Number(prices[i] ?? it.price);
                        return (
                          <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium text-gray-800">
                              {it?.productId?.name} ({it?.productId?.ref})
                            </td>

                            <td className="py-3 px-4 text-right">
                              <input
                                type="number"
                                min={0}
                                value={isNaN(q) ? 0 : q}
                                onChange={(e) => setQuantities(prev => ({ ...prev, [i]: Number(e.target.value) }))}
                                className="w-24 text-right rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                disabled={isLocked}
                              />
                            </td>

                            <td className="py-3 px-4 text-right align-top">
                              <div className="flex flex-col items-end">
                                <input
                                  type="number"
                                  min={0}
                                  step="0.1"
                                  value={isNaN(pu) ? 0 : pu}
                                  onChange={(e) =>
                                    setPrices((prev) => ({ ...prev, [i]: Number(e.target.value) }))
                                  }
                                  className="w-28 text-right rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                  disabled={isLocked}
                                />
                                {priceUpdated[i] && (
                                  <span className="mt-1 text-[11px] text-green-500 bg-emerald-50 rounded px-2 py-0.5">
                                    Prix d'achat produit maj
                                  </span>
                                )}
                              </div>
                            </td>


                            <td className="py-3 px-4 text-right">
                              {formatMoney((isNaN(pu) ? 0 : pu) * (isNaN(q) ? 0 : q))}
                            </td>

                            <td className="py-3 px-4">
                              <input
                                type="text"
                                value={lotNumbers[i] || ''}
                                onChange={(e) => setLotNumbers(prev => ({ ...prev, [i]: e.target.value }))}
                                placeholder="Numéro de lot"
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                disabled={isLocked}
                              />
                            </td>

                            <td className="py-3 px-4">
                              <input
                                type="date"
                                value={dlcs[i] || ''}
                                onChange={(e) => setDlcs(prev => ({ ...prev, [i]: e.target.value }))}
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                disabled={isLocked}
                              />
                            </td>

                            <td className="py-3 px-4">
                              <select
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none bg-white"
                                value={areas[i] || 'zone-a'}
                                onChange={(e) => setAreas(prev => ({ ...prev, [i]: e.target.value }))}
                                disabled={isLocked}
                              >
                                {[
                                  { v: 'zone-a', l: 'Zone A' },
                                  { v: 'zone-b', l: 'Zone B' },
                                  { v: 'zone-c', l: 'Zone C' },
                                ].map((item, index) => (
                                  <option key={index} value={item.v}>{item.l}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                      {(!po.items || po.items.length === 0) && (
                        <tr>
                          <td colSpan={7} className="py-4 text-center text-gray-400">Aucun article</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Récap */}
              <div className="flex justify-end mt-4">
                <div className="text-sm text-navy-700 flex flex-col items-end gap-0.5">
                  <div className="flex gap-4"><span className="text-gray-500">Sous-total articles (HT)</span><span className="font-medium">{formatMoney(totalArticlesHT)}</span></div>
                  <div className="flex gap-4"><span className="text-gray-500">Frais de livraison</span><span className="font-medium">{formatMoney(Number(po?.deliveryFees ?? 0))}</span></div>
                  <div className="flex gap-4"><span className="text-gray-500">TVA totale</span><span className="font-medium">{formatMoney(totalTVA)}</span></div>
                  <div className="flex gap-4 border-t pt-1 mt-1"><span className="font-semibold">Total TTC</span><span className="font-semibold">{formatMoney(totalTTC)}</span></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-200">
              {!po.pay && (
                  <button onClick={handleMarkPaid} disabled={markingPaid} className="px-4 py-2 bg-emerald-500 text-emerald-500 border border-emerald-200 rounded-lg hover:bg-emerald-600 text-xs flex items-center gap-2 disabled:opacity-50">
                     {markingPaid ? 'Marquage…' : 'Marquer payé'}
                   </button>
                )}

                <button
                  onClick={po.status === 'pending' ? handleMarkReceived : handleSaveReception}
                  disabled={(po.status === 'pending' ? markingReceived || savingReception : savingReception) || isLocked}
                  className="px-4 py-2 bg-[#0260CB] text-white rounded-lg hover:bg-[#01499b] flex items-center gap-2 text-xs disabled:opacity-50"
                >
                  {po.status === 'pending' ? (markingReceived ? 'Enregistrement…' : 'Marquer reçu') : (savingReception ? 'Mise à jour…' : 'Mettre à jour réception')}
                </button>

                <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs">Fermer</button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default UpdatePurchaseModal;
